<template>
  <div ref="containerRef">
    <div
      class="c-sticky-navigation u-flex u-flex-justify-center"
      :class="{ 'is-header-hidden': isHeaderHidden }"
      :style="{ top: stickyBarOffset }"
    >
      <div
        ref="scrollerRef"
        class="c-sticky-navigation__list-scroller"
        :class="{
          'u-width-100 c-sticky-navigation__list-scroller--no-scroll': sections.length < 5
            && $mq !== 'desktop'
        }"
      >
        <nav
          :aria-label="label"
          class="c-sticky-navigation__list-wrap"
        >
          <ul
            class="c-sticky-navigation__list u-bare-list"
            :class="{
              'u-flex-justify-evenly': sections.length < 5 && $mq !== 'desktop'
            }"
          >
            <li
              v-for="(item, index) in sections"
              :key="item.anchorTag"
              class="c-sticky-navigation__list-item"
            >
              <a
                :href="`#${item.anchorTag}`"
                class="u-flex u-flex-direction-column"
                :class="{
                  'is-active': index === activeIndex
                }"
                @click.prevent="scrollToBlock(item)"
              >
                <span class="c-sticky-navigation__list-item-icon u-ml--a u-mr--a">
                  <img
                    v-if="item.icon"
                    :src="item.icon"
                    :alt="item.anchorTitle"
                  >
                </span>
                {{ isNotDesktop ? item.mobileAnchorTitle : item.anchorTitle }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref
} from 'vue'

export default {
  props: {
    label: {
      type: String,
      default: 'Sticky Navigation'
    },
    items: {
      type: Array,
      required: true
    }
  },
  setup(props) {
    const containerRef = ref(null)
    const scrollerRef = ref(null)
    const { proxy } = getCurrentInstance()
    const isNotDesktop = computed(() => proxy.$mq === 'mobile' || proxy.$mq === 'tablet-x')
    const activeIndex = ref(-1)
    const stickyBarOffset = ref('auto')
    const header = document.querySelector('.c-header')

    let preventUpdateDuringScroll = false
    const isHeaderHidden = ref(header.classList.contains('is-hidden'))

    const watchHeader = () => {
      const config = { attributes: true }

      const callback = function (mutations) {
        mutations.forEach((mutation) => {
          const { target } = mutation

          if (mutation.attributeName === 'class') {
            isHeaderHidden.value = target.classList.contains('is-hidden')
          }
        })
      }
      const observer = new MutationObserver(callback)
      observer.observe(header, config)
    }

    // Create an observer instance linked to the callback function

    const calculateOffset = () => {
      const isDesktop = !isNotDesktop.value
      const headerHeight = header.offsetHeight

      if (isDesktop && containerRef.value.getBoundingClientRect().top < 100) {
        stickyBarOffset.value = `${headerHeight}px`
      } else {
        stickyBarOffset.value = '150px'
      }
    }

    const scrollToBlock = (item, instantScroll = false) => {
      const element = document.querySelector(`[data-id="${item.anchorTag}"]`)

      if (element) {
        const topOffset = element.getBoundingClientRect().top + window.scrollY - header.offsetHeight
        preventUpdateDuringScroll = true
        window.scrollTo({
          top: topOffset,
          left: 0,
          behavior: instantScroll ? 'auto' : 'smooth'
        })
        setTimeout(() => {
          preventUpdateDuringScroll = false
        }, 200)
      }
    }

    const sections = props.items.map(({
      anchorTag, icon, anchorTitle, mobileAnchorTitle
    }) => ({
      element: document.querySelector(`[data-id="${anchorTag}"]`),
      anchorTag,
      icon,
      anchorTitle,
      mobileAnchorTitle
    })).filter(({ anchorTag, anchorTitle, element }) => anchorTag && anchorTitle && element)

    const offset = computed(() => {
      if (proxy.$mq === 'desktop') {
        return 250
      }
      return 200
    })

    const updateIndex = (item) => {
      if (item.index) {
        activeIndex.value = item.index
      } else {
        activeIndex.value = sections.findIndex(({ anchorTag }) => anchorTag === item.anchorTag)
      }
      window.location.hash = `#${item.anchorTag}`
    }

    const updateActiveIndexOnScroll = () => {
      if (!preventUpdateDuringScroll) {
        const offsets = sections.map(({ element, anchorTag }, index) => {
          const { y } = element.getBoundingClientRect()

          return {
            y, anchorTag, index
          }
        })
          .filter((item) => item.y <= offset.value)
          .sort((a, b) => a.y - b.y)

        if (offsets.length) {
          const activeAnchor = offsets[offsets.length - 1]
          updateIndex(activeAnchor)
          setTimeout(() => {
            const activeLink = containerRef.value.querySelector('.is-active')
            if (activeLink) {
              activeLink.scrollIntoViewIfNeeded()
            }
          }, 0)
        } else {
          activeIndex.value = -1
          window.location.hash = '#'
        }
      }
    }

    const scrollOffset = 20

    const showShadowsIfNeeded = () => {
      const checkScroll = () => {
        if (!scrollerRef.value || !containerRef.value) {
          return
        }
        const { scrollLeft, offsetWidth, scrollWidth } = scrollerRef.value
        if (scrollLeft > scrollOffset) {
          containerRef.value.classList.add('has-left-shadow')
        } else {
          containerRef.value.classList.remove('has-left-shadow')
        }
        if (scrollWidth - (scrollLeft + offsetWidth) < scrollOffset) {
          containerRef.value.classList.remove('has-right-shadow')
        } else {
          containerRef.value.classList.add('has-right-shadow')
        }
      }
      checkScroll()
      scrollerRef.value.addEventListener('scroll', checkScroll)
    }

    onMounted(() => {
      window.addEventListener('scroll', calculateOffset)
      calculateOffset()
      if (window.location.hash) {
        const hash = window.location.hash.substring(1)
        const activeItem = props.items.find(({ anchorTag }) => anchorTag === hash)
        if (activeItem) {
          scrollToBlock(activeItem, true)
          updateIndex(activeItem)
        }
      }
      window.addEventListener('scroll', updateActiveIndexOnScroll)
      updateActiveIndexOnScroll()
      watchHeader()
      showShadowsIfNeeded()
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', updateActiveIndexOnScroll)
      window.removeEventListener('scroll', calculateOffset)
    })

    return {
      containerRef,
      scrollerRef,
      isNotDesktop,
      activeIndex,
      stickyBarOffset,
      sections,
      isHeaderHidden,

      scrollToBlock
    }
  }
}
</script>
